import L from "leaflet";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import { Box, FormControlLabel, IconButton, Switch } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { FIELD_STATUS_ALL_CLASSIFICATIONS } from "../../utils/fieldDataDiseaseStatusUtils";
import SensorHeatmapControlContent from "./sensorHeatmapControlContent.component";

const selectStyle = {
  width: "180px",
  minWidth: "0",
  padding: "4px",
  border: "1px solid rgba(0,0,0,0.2)",
  borderRadius: "4px",
  fontSize: "12px",
};
const switchStyle = {
  flexShrink: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "12px",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  "&.MuiFormControlLabel-root": { marginLeft: 0, marginRight: 0 },
};

const ControlSwitch = ({ label, checked, onChange, disabled = false }) => (
  <FormControlLabel
    label={label}
    labelPlacement="start"
    sx={switchStyle}
    control={
      <Switch
        checked={checked}
        onChange={(event) => onChange?.(event.target.checked)}
        disabled={disabled}
        size="small"
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": { color: "#216557" },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#4d8378",
          },
        }}
      />
    }
  />
);

// A portal keeps the app's theme/context and the same Leaflet control across updates.
const FieldStatusControl = ({
  isEnabled,
  onEnabledChange,
  classification = FIELD_STATUS_ALL_CLASSIFICATIONS,
  onClassificationChange,
  classificationOptions = [],
  heatmap,
  opacity = 0.7,
  onOpacityChange,
}) => {
  const map = useMap();
  const [isExpanded, setIsExpanded] = useState(true);
  const [container] = useState(() =>
    L.DomUtil.create("div", "field-status-control"),
  );
  useEffect(() => {
    const control = L.control({ position: "topleft" });
    control.onAdd = () => container;
    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);
    L.DomEvent.on(container, "keydown", L.DomEvent.stopPropagation);
    const resize = () => {
      container.style.maxWidth = `${Math.max(0, map.getSize().x - 20)}px`;
    };
    resize();
    map.on("resize", resize);
    control.addTo(map);
    return () => {
      map.off("resize", resize);
      control.remove();
      L.DomEvent.off(container);
    };
  }, [container, map]);

  return createPortal(
    <Box
      sx={{
        width: heatmap ? "300px" : isEnabled ? "fit-content" : "160px",
        maxWidth: "100%",
        maxHeight: "calc(70vh - 24px)",
        boxSizing: "border-box",
        minHeight: "44px",
        background: "white",
        border: "1px solid rgba(0,0,0,0.2)",
        borderRadius: "4px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
        padding: "8px",
        display: "flex",
        flexDirection: heatmap ? "column" : "row",
        alignItems: heatmap ? "stretch" : "center",
        gap: "8px",
        overflow: "auto",
        fontSize: "12px",
        color: "#333",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <ControlSwitch
          label="Stato campo"
          checked={isEnabled}
          onChange={onEnabledChange}
          disabled={!onEnabledChange}
        />
        {heatmap && (
          <span title={heatmap.disabledReason || undefined}>
            <ControlSwitch
              label="Heatmap"
              checked={heatmap.enabled}
              onChange={heatmap.onEnabledChange}
              disabled={Boolean(heatmap.disabledReason)}
            />
          </span>
        )}
        {heatmap?.enabled && (
          <IconButton
            size="small"
            aria-label={
              isExpanded
                ? "Riduci controlli heatmap"
                : "Espandi controlli heatmap"
            }
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>
      {isEnabled && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap={heatmap ? "wrap" : "nowrap"}
        >
          <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            Ultima acquisizione
          </span>
          <select
            aria-label="Classe avversita"
            value={classification}
            style={selectStyle}
            onChange={(event) => onClassificationChange?.(event.target.value)}
          >
            <option value={FIELD_STATUS_ALL_CLASSIFICATIONS}>
              Tutte le classi
            </option>
            {classificationOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Box>
      )}
      {heatmap?.disabledReason && <span>{heatmap.disabledReason}</span>}
      {heatmap?.enabled && (
        <Box display={isExpanded ? "block" : "none"}>
          <SensorHeatmapControlContent
            heatmap={heatmap}
            opacity={opacity}
            onOpacityChange={onOpacityChange}
          />
        </Box>
      )}
    </Box>,
    container,
  );
};

export default FieldStatusControl;
